import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { AppLayout } from '@/layouts/AppLayout';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { Dashboard } from '@/pages/Dashboard';
import { Playground } from '@/pages/Playground';
import { GovernanceScanner } from '@/pages/Governance';
import { Usage } from '@/pages/Usage';
import { Marketplace } from '@/pages/Marketplace';
import { MarketplaceDetail } from '@/pages/MarketplaceDetail';
import { MarketplacePublish } from '@/pages/MarketplacePublish';
import { MyListings } from '@/pages/MyListings';
import { Academy } from '@/pages/Academy';
import { Team } from '@/pages/Team';
import { ApiKeys } from '@/pages/ApiKeys';
import { SettingsPage } from '@/pages/SettingsPage';
import { SsoCallback } from '@/pages/SsoCallback';
import { CustomGpts } from '@/pages/CustomGpts';
import { Agents } from '@/pages/Agents';
import { AgentBuilder } from '@/pages/AgentBuilder';
import { AgentDetail } from '@/pages/AgentDetail';
import { AgentTemplates } from '@/pages/AgentTemplates';
import { WorkDNA } from '@/pages/WorkDNA';
import { Strategy } from '@/pages/Strategy';
import { Content } from '@/pages/Content';
import { Workflows } from '@/pages/Workflows';
import { SecurityPage } from '@/pages/SecurityPage';
import { VoicePage } from '@/pages/VoicePage';
import { TheOneAI } from '@/pages/TheOneAI';
import { AIMemory } from '@/pages/AIMemory';

// Governance pages
import { GovernanceDashboard } from '@/pages/governance/GovernanceDashboard';
import { PoliciesPage } from '@/pages/governance/PoliciesPage';
import { RiskDashboardPage } from '@/pages/governance/RiskDashboardPage';
import { CompliancePage } from '@/pages/governance/CompliancePage';
import { IncidentsPage } from '@/pages/governance/IncidentsPage';
import { ShadowAIPage } from '@/pages/governance/ShadowAIPage';
import { CertificationsPage } from '@/pages/governance/CertificationsPage';
import { LegalAdvisoryPage } from '@/pages/governance/LegalAdvisoryPage';
import { ModelsPage } from '@/pages/governance/ModelsPage';
import { AssetsPage } from '@/pages/governance/AssetsPage';
import { DeploymentsPage } from '@/pages/governance/DeploymentsPage';
import { EvidencePage } from '@/pages/governance/EvidencePage';
import { CostTrackingPage } from '@/pages/governance/CostTrackingPage';
import { IntelligencePage } from '@/pages/governance/IntelligencePage';

// Studio pages
import { Builder } from '@/pages/studio/Builder';
import { MyApps } from '@/pages/studio/MyApps';
import { AppDetail } from '@/pages/studio/AppDetail';
import { StarterLibrary } from '@/pages/studio/StarterLibrary';
import { IdeaLibrary } from '@/pages/studio/IdeaLibrary';
import { ClientTenants } from '@/pages/studio/ClientTenants';
import { SecurityPosture } from '@/pages/studio/SecurityPosture';
import { PSAConnection } from '@/pages/studio/PSAConnection';
import { StudioBilling } from '@/pages/studio/StudioBilling';
import { StudioOnboarding } from '@/pages/studio/StudioOnboarding';
import { StudioSubscribe } from '@/pages/studio/StudioSubscribe';
import { AIDivisionSetup } from '@/pages/studio/AIDivisionSetup';

import { type ReactNode } from 'react';
import { ToastProvider, AnimatedPage } from '@theonefamily/ui';
import ErrorCaptureWrapper from '@/error-capture/ErrorCaptureWrapper';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/sso" element={<SsoCallback />} />
      <Route path="/studio/subscribe" element={<StudioSubscribe />} />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Core */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/team" element={<Team />} />
        <Route path="/api-keys" element={<ApiKeys />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* Build */}
        <Route path="/apps" element={<MyApps />} />
        <Route path="/apps/:id" element={<AppDetail />} />
        <Route path="/builder/:projectId" element={<Builder />} />
        <Route path="/starters" element={<StarterLibrary />} />
        <Route path="/ideas" element={<IdeaLibrary />} />
        <Route path="/clients" element={<ClientTenants />} />
        <Route path="/clients/:projectId/security" element={<SecurityPosture />} />
        <Route path="/psa" element={<PSAConnection />} />
        <Route path="/onboarding" element={<StudioOnboarding />} />
        <Route path="/studio/ai-division" element={<AIDivisionSetup />} />
        <Route path="/studio/billing" element={<StudioBilling />} />

        {/* AI Chat */}
        <Route path="/ai" element={<TheOneAI />} />
        <Route path="/ai/memory" element={<AIMemory />} />

        {/* AI Tools */}
        <Route path="/playground" element={<Playground />} />
        <Route path="/custom-gpts" element={<CustomGpts />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/agents/new" element={<AgentBuilder />} />
        <Route path="/agents/templates" element={<AgentTemplates />} />
        <Route path="/agents/:agentId" element={<AgentDetail />} />
        <Route path="/agents/:agentId/edit" element={<AgentBuilder />} />
        <Route path="/content" element={<Content />} />
        <Route path="/workflows" element={<Workflows />} />

        {/* Governance */}
        <Route path="/governance" element={<GovernanceDashboard />} />
        <Route path="/governance/scanner" element={<GovernanceScanner />} />
        <Route path="/governance/policies" element={<PoliciesPage />} />
        <Route path="/governance/risk" element={<RiskDashboardPage />} />
        <Route path="/governance/compliance" element={<CompliancePage />} />
        <Route path="/governance/evidence" element={<EvidencePage />} />
        <Route path="/governance/incidents" element={<IncidentsPage />} />
        <Route path="/governance/shadow-ai" element={<ShadowAIPage />} />
        <Route path="/governance/certifications" element={<CertificationsPage />} />
        <Route path="/governance/legal" element={<LegalAdvisoryPage />} />
        <Route path="/governance/models" element={<ModelsPage />} />
        <Route path="/governance/assets" element={<AssetsPage />} />
        <Route path="/governance/deployments" element={<DeploymentsPage />} />
        <Route path="/governance/costs" element={<CostTrackingPage />} />
        <Route path="/governance/intelligence" element={<IntelligencePage />} />

        {/* Intelligence */}
        <Route path="/workdna" element={<WorkDNA />} />
        <Route path="/strategy" element={<Strategy />} />

        {/* Ecosystem */}
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
        <Route path="/marketplace/publish" element={<MarketplacePublish />} />
        <Route path="/marketplace/my-listings" element={<MyListings />} />
        <Route path="/academy" element={<Academy />} />

        {/* Products */}
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/voice" element={<VoicePage />} />

      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <AnimatedPage>
              <ErrorCaptureWrapper>
                <AppRoutes />
              </ErrorCaptureWrapper>
            </AnimatedPage>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
